import { AvailabilityMessageStyled } from './ItemInfo.style';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import { ItemName } from 'components/Pages/Cart/List/Item/ItemName';
import { useSettings } from 'connectors/Settings/Settings';
import { getStoresAvailabilityMessageQuantityDependent } from 'helpers/availability/pickupPointAvailability';
import { getLastDayFromPersonalPickupString } from 'helpers/getLastDayFromPersonalPickupString';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import Skeleton from 'react-loading-skeleton';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum, StoreListAvailabilityStatusEnum } from 'types/availability';
import { CartItemType } from 'types/cart';

type ItemInfoProps = {
    item: CartItemType;
    fetching?: boolean;
};

const ItemInfo: FC<ItemInfoProps> = ({ item, fetching, className }) => {
    const testIdentifier = 'pages-cart-list-item-iteminfo-';
    const domainConfig = useDomainConfig();
    const settings = useSettings();
    const { defaultStoreUuid, changeTransportFetching } = usePersistStore((s) => s);

    const t = useTypedTranslationFunction();
    const defaultStoreAvailability = item.product.defaultStoreAvailability;
    const isElectronicGiftVoucher = item.product.isElectronicGiftVoucher;

    const availabilityStatus =
        item.product.availability.status === AvailabilityStatusEnum.inStock
            ? item.quantity > item.product.centralStockQuantity
                ? t('Available {{ quantity }} pcs in eshop', { quantity: item.product.centralStockQuantity })
                : t('Available in eshop')
            : t('Unavailable in eshop');

    const isInCentralStoreWithEnabledReservationsAndNowhereElse =
        item.product.deliveryDaysFromCentralStore !== null &&
        item.product.centralStockQuantity >= item.quantity &&
        item.availableStoresCountByQuantity === 0;

    const isInCentralStoreWithEnabledReservations =
        item.product.deliveryDaysFromCentralStore !== null && item.product.centralStockQuantity >= item.quantity;

    const isNotAvailableAnywhere =
        item.availableStoresCountByQuantity === 0 && !isInCentralStoreWithEnabledReservationsAndNowhereElse;

    const count = item.availableStoresCountByQuantity;
    const { days, daysCount } = getLastDayFromPersonalPickupString(item.product.deliveryDaysFromCentralStore);

    const getDefaultStoreAvailabilityMessage = () => {
        if (!defaultStoreAvailability) {
            return null;
        }

        const { storeName, availabilityStatus } = defaultStoreAvailability;
        const { defaultStoreQuantity, deliveryDaysFromCentralStore } = item.product;

        if (availabilityStatus === StoreListAvailabilityStatusEnum.inStock) {
            if (defaultStoreQuantity !== null && item.quantity > defaultStoreQuantity) {
                if (deliveryDaysFromCentralStore !== null && item.product.centralStockQuantity >= item.quantity) {
                    return `${t('Available {{ quantity }} pcs', { quantity: defaultStoreQuantity })} ${t(
                        'in {{ store }}',
                        { store: storeName },
                    )} ${t('or all in {{ days }} days', { days, count: daysCount })}`;
                }

                return `${t('Available {{ quantity }} pcs', { quantity: defaultStoreQuantity })} ${t('in {{ store }}', {
                    store: storeName,
                })}`;
            }

            return t('Available in {{ store }}', { store: storeName });
        } else if (isInCentralStoreWithEnabledReservations) {
            return t('Available in {{ store }} in {{ days }} days', { store: storeName, days, count: daysCount });
        } else if (item.availableStoresCountByQuantity > 0) {
            return t('Unavailable in {{ store }}', { store: storeName });
        }

        return t('Unavailable in stores');
    };

    return (
        <>
            <ItemName item={item} />
            <div
                className={twJoin(
                    'my-2 block min-w-[40%] text-left',
                    isB2bDomain(domainConfig) ? 'min-w-fit' : 'min-w-[40%]',
                    className,
                )}
                data-testid={testIdentifier + 'availability'}
            >
                {!fetching && !isB2bDomain(domainConfig) && (
                    <>
                        <AvailabilityMessageStyled
                            isNotAvailable={item.product.availability.status !== AvailabilityStatusEnum.inStock}
                        >
                            {availabilityStatus}
                        </AvailabilityMessageStyled>
                        {defaultStoreAvailability && !changeTransportFetching ? (
                            <AvailabilityMessageStyled
                                isNotAvailable={
                                    defaultStoreAvailability.quantity < item.quantity &&
                                    !isInCentralStoreWithEnabledReservations
                                }
                            >
                                {getDefaultStoreAvailabilityMessage()}
                            </AvailabilityMessageStyled>
                        ) : (
                            defaultStoreUuid && (
                                <AvailabilityMessageStyled isNotAvailable={false}>
                                    <Skeleton className="h-3 w-4/6" />
                                </AvailabilityMessageStyled>
                            )
                        )}
                        {!isElectronicGiftVoucher && (
                            <AvailabilityMessageStyled isNotAvailable={isNotAvailableAnywhere}>
                                {getStoresAvailabilityMessageQuantityDependent(
                                    isInCentralStoreWithEnabledReservationsAndNowhereElse,
                                    count,
                                    item.product.deliveryDaysFromCentralStore,
                                    item.quantity,
                                    item.product.availableStoresCount,
                                    settings?.pickupPointCount,
                                    t,
                                )}
                            </AvailabilityMessageStyled>
                        )}
                    </>
                )}
                {!fetching && isB2bDomain(domainConfig) && (
                    <ProductStock
                        quantity={item.product.stockQuantity}
                        minimumSellingQuantity={item.product.b2bMinimumProductOrder ?? 1}
                    />
                )}
                {fetching && (
                    <>
                        <Skeleton className="mb-1 h-3 w-1/2" />
                        {defaultStoreAvailability && <Skeleton className="mb-1 h-3 w-4/6" />}
                        <Skeleton className="mb-1 h-3 w-44 vl:w-3/4" />
                    </>
                )}
            </div>
        </>
    );
};

export default ItemInfo;
